import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowRight, 
  CheckCircle2,
  Play,
  Star,
  Zap,
  Clock,
  TrendingUp,
  Calendar,
  DollarSign,
  Users,
  Layers,
  Package,
  BarChart3
} from 'lucide-react';

// Campaign images - served from /public/campaign so they survive CDN issues
const CAMPAIGN_IMAGES = {
  softwareEngineer: '/campaign/jewelry-maker.png',
  accountant: '/campaign/pottery-artist.png',
  dataAnalyst: '/campaign/candle-maker.png',
  marketer: '/campaign/watercolor-artist.png',
  customerService: '/campaign/soap-maker.png',
};

// Hero carousel slides
const heroSlides = [
  {
    image: CAMPAIGN_IMAGES.softwareEngineer,
    role: 'SOFTWARE ENGINEER',
    tagline: 'Simple tools. Zero tech headaches.',
    cta: 'Skip the Setup',
    cropPosition: 'center top',
    overlayStrength: 'from-black/70 via-black/30 to-black/20',
  },
  {
    image: CAMPAIGN_IMAGES.accountant,
    role: 'ACCOUNTANT',
    tagline: "We'll handle the numbers. You handle the magic.",
    cta: 'Create More, Count Less',
    cropPosition: 'center top',
    overlayStrength: 'from-black/70 via-black/30 to-black/20',
  },
  {
    image: CAMPAIGN_IMAGES.dataAnalyst,
    role: 'DATA ANALYST',
    tagline: 'Ditch the dashboards. Make what matters.',
    cta: 'Automate the Data',
    cropPosition: 'center top',
    overlayStrength: 'from-black/70 via-black/30 to-black/20',
  },
  {
    image: CAMPAIGN_IMAGES.customerService,
    role: 'CUSTOMER SERVICE REP',
    tagline: 'Stop juggling messages. Start making sales.',
    cta: 'Sell Smarter',
    cropPosition: 'center top',
    overlayStrength: 'from-black/70 via-black/30 to-black/20',
  },
  {
    image: CAMPAIGN_IMAGES.marketer,
    role: 'MARKETER',
    tagline: 'Sell smarter, not harder.',
    cta: 'Market Less, Make More',
    cropPosition: 'center top',
    overlayStrength: 'from-black/70 via-black/30 to-black/20',
  },
];

// Animated typing component for role text
const AnimatedRole = ({ role, isActive }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  useEffect(() => {
    if (!isActive) {
      setDisplayedText('');
      return;
    }
    
    setIsTyping(true);
    setDisplayedText('');
    
    let i = 0;
    const typingSpeed = 60; // ms per character
    
    const typeInterval = setInterval(() => {
      if (i < role.length) {
        setDisplayedText(role.slice(0, i + 1));
        i++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
      }
    }, typingSpeed);
    
    return () => clearInterval(typeInterval);
  }, [role, isActive]);
  
  return (
    <span className="relative inline-block min-w-[200px]">
      <span className="text-[#4ADE80]">{displayedText}</span>
      {isTyping && (
        <span className="absolute ml-1 animate-blink text-[#4ADE80]">|</span>
      )}
    </span>
  );
};

export const Home = () => {
  const { user } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const heroRef = useRef(null);

  // Auto-advance hero slides
  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  const features = [
    {
      icon: Calendar,
      title: 'Event Discovery & Planning',
      desc: 'AI finds the perfect shows for your craft. Never miss an application deadline again.',
    },
    {
      icon: DollarSign,
      title: 'Smart Pricing',
      desc: 'Know exactly what to charge. AI analyzes materials, labor, and market to maximize profit.',
    },
    {
      icon: Layers,
      title: 'Multi-Channel Sales',
      desc: 'Etsy, boutiques, events, website—track every sale and see true profit after ALL fees.',
    },
    {
      icon: BarChart3,
      title: 'Business Analytics',
      desc: 'Real insights, not spreadsheet headaches. See what sells, where, and why.',
    },
  ];

  const testimonials = [
    { 
      quote: "I went from spending weekends on spreadsheets to actually making jewelry. My sales are up 40% because I finally have time to create.", 
      name: "Sarah M.", 
      craft: "Jewelry Designer",
      stars: 5
    },
    { 
      quote: "Found 3 amazing shows I never knew existed. The AI matching is scary accurate—it knows my craft better than I do!", 
      name: "Marcus T.", 
      craft: "Woodworker",
      stars: 5
    },
    { 
      quote: "No more guessing prices. No more missed deadlines. I'm an artist again, not an unpaid accountant.", 
      name: "Elena R.", 
      craft: "Ceramics Artist",
      stars: 5
    },
  ];

  const stats = [
    { value: '10K+', label: 'Artisans Trust Us' },
    { value: '40%', label: 'Avg. Profit Increase' },
    { value: '15hrs', label: 'Saved Per Month' },
    { value: '4.9', label: 'Star Rating', icon: Star },
  ];

  return (
    <div className="overflow-hidden bg-white">
      {/* Hero Section - Full Width Campaign Image */}
      <section ref={heroRef} className="relative min-h-[90vh] flex flex-col">
        {/* Campaign Images - Cropped to show only visual content */}
        <div className="relative flex-1 overflow-hidden">
          {heroSlides.map((slide, idx) => (
            <div
              key={idx}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                currentSlide === idx ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img 
                src={slide.image}
                alt={`Artisan vs ${slide.role}`}
                className="w-full h-full object-cover"
                style={{ 
                  objectPosition: slide.cropPosition,
                  minHeight: '90vh',
                }}
              />
            </div>
          ))}
          
          {/* Dark gradient overlay for text readability - dynamic per slide */}
          <div className={`absolute inset-0 bg-gradient-to-t ${heroSlides[currentSlide].overlayStrength} z-20`} />
          
          {/* Hero Content - Bottom Positioned */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 lg:p-16 z-30">
            <div className="max-w-7xl mx-auto">
              {/* Main Headline with Animated Role */}
              <div className="mb-6 overflow-hidden">
                <h1 
                  className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-tight text-white"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  <span className="block animate-slideUp">YOU'RE AN ARTIST,</span>
                  <span className="block">
                    NOT {/^[AEIOU]/i.test(heroSlides[currentSlide].role) ? 'AN' : 'A'}{' '}
                    <AnimatedRole 
                      role={heroSlides[currentSlide].role} 
                      isActive={true}
                    />
                  </span>
                </h1>
              </div>
              
              {/* Tagline with fade animation */}
              <div className="overflow-hidden">
                <p 
                  key={currentSlide}
                  className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl font-medium animate-fadeSlideUp"
                >
                  {heroSlides[currentSlide].tagline}
                </p>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8 animate-fadeSlideUp" style={{ animationDelay: '0.3s' }}>
                <Link to={user ? '/events' : '/signup'}>
                  <Button 
                    className="bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold text-lg px-8 py-6 rounded-lg shadow-xl uppercase tracking-wide transition-all hover:scale-105 hover:shadow-2xl"
                    data-testid="hero-cta"
                  >
                    {user ? 'Go to Dashboard' : heroSlides[currentSlide].cta}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                {!user && (
                  <Link to="/login">
                    <Button 
                      variant="outline"
                      className="bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white border-2 border-white/50 font-bold text-lg px-8 py-6 rounded-lg transition-all hover:border-white"
                    >
                      Sign In
                    </Button>
                  </Link>
                )}
              </div>
              
              {/* Trust indicators */}
              <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm animate-fadeIn" style={{ animationDelay: '0.5s' }}>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E]" /> 
                  7-day free trial
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E]" /> 
                  No credit card required
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#22C55E]" /> 
                  Cancel anytime
                </span>
              </div>
            </div>
          </div>
          
          {/* Slide Navigation Dots */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex items-center gap-3 z-30">
            {heroSlides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => { setCurrentSlide(idx); setIsAutoPlaying(false); }}
                className={`rounded-full transition-all duration-300 ${
                  currentSlide === idx 
                    ? 'w-10 h-3 bg-[#22C55E]' 
                    : 'w-3 h-3 bg-white/50 hover:bg-white/70'
                }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Logo Bar / Brand Strip */}
      <section className="py-8 px-6 bg-[#1E3A5F]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img 
              src="/ams_compass_logo.png" 
              alt="AMSCompass" 
              className="h-12 w-auto"
            />
            <div>
              <span className="text-2xl font-black text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>AMS</span>
              <span className="text-2xl font-black text-[#4ADE80]" style={{ fontFamily: "'Outfit', sans-serif" }}>Compass</span>
            </div>
          </div>
          <p className="text-white/80 text-center md:text-right text-lg">
            The business tool built <span className="text-[#4ADE80] font-semibold">by artisans, for artisans</span>
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 px-6 bg-gray-50 border-y border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <span className="text-4xl md:text-5xl font-black text-[#1E3A5F]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {stat.value}
                  </span>
                  {stat.icon && <stat.icon className="w-6 h-6 text-yellow-500 fill-yellow-500" />}
                </div>
                <p className="text-gray-600 font-medium">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem/Solution Section with Campaign Images */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black text-[#1E3A5F] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Stop Wearing All The Hats
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              You didn't start making beautiful things to spend your life in spreadsheets. 
              Let AMSCompass handle the business stuff so you can get back to creating.
            </p>
          </div>

          {/* Campaign Image Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Accountant Card */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
              <div className="h-80 overflow-hidden">
                <img 
                  src={CAMPAIGN_IMAGES.accountant}
                  alt="Artisan vs Accountant"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Automate Your Finances
                </h3>
                <p className="text-white/80 mb-4">
                  Track sales, calculate true profit after fees, and see where your money actually goes.
                </p>
                <span className="inline-block px-4 py-2 bg-[#22C55E] text-white font-bold rounded-lg text-sm uppercase">
                  Create More, Count Less
                </span>
              </div>
            </div>

            {/* Data Analyst Card */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
              <div className="h-80 overflow-hidden">
                <img 
                  src={CAMPAIGN_IMAGES.dataAnalyst}
                  alt="Artisan vs Data Analyst"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Ditch the Dashboards
                </h3>
                <p className="text-white/80 mb-4">
                  All your data in one beautiful place. No complex analytics. Just insights that matter.
                </p>
                <span className="inline-block px-4 py-2 bg-[#22C55E] text-white font-bold rounded-lg text-sm uppercase">
                  Automate the Data
                </span>
              </div>
            </div>

            {/* Customer Service Card */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
              <div className="h-80 overflow-hidden">
                <img 
                  src={CAMPAIGN_IMAGES.customerService}
                  alt="Artisan vs Customer Service"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Stop Juggling Messages
                </h3>
                <p className="text-white/80 mb-4">
                  Manage customer inquiries effortlessly. Spend less time on emails, more time creating.
                </p>
                <span className="inline-block px-4 py-2 bg-[#22C55E] text-white font-bold rounded-lg text-sm uppercase">
                  Sell Smarter
                </span>
              </div>
            </div>

            {/* Marketer Card */}
            <div className="group relative overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1">
              <div className="h-80 overflow-hidden">
                <img 
                  src={CAMPAIGN_IMAGES.marketer}
                  alt="Artisan vs Marketer"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  Find Your Perfect Events
                </h3>
                <p className="text-white/80 mb-4">
                  AI-powered event matching finds shows where your products will sell. No more guessing.
                </p>
                <span className="inline-block px-4 py-2 bg-[#22C55E] text-white font-bold rounded-lg text-sm uppercase">
                  Market Less, Make More
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black text-[#1E3A5F] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Everything You Need. Nothing You Don't.
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Built specifically for artisan entrepreneurs. No bloated features. Just the tools you'll actually use.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <div 
                key={feature.title}
                className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow border border-gray-100"
              >
                <div className="w-14 h-14 rounded-xl bg-[#1E3A5F] flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-bold text-xl text-[#1E3A5F] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Software Engineer Hero Banner - Full Width */}
      <section className="relative overflow-hidden">
        <div className="h-[500px] overflow-hidden">
          <img 
            src={CAMPAIGN_IMAGES.softwareEngineer}
            alt="Artisan vs Software Engineer"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
            <div className="max-w-xl">
              <h2 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Simple Tools.
                <br />
                <span className="text-[#4ADE80]">Zero Tech Headaches.</span>
              </h2>
              <p className="text-xl text-white/90 mb-8">
                No coding required. No complex setup. Just sign up and start running your business smarter.
              </p>
              <Link to={user ? '/events' : '/signup'}>
                <Button 
                  className="bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold text-lg px-8 py-6 rounded-lg shadow-xl uppercase tracking-wide transition-all hover:scale-105"
                >
                  Skip the Setup
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-black text-[#1E3A5F] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Artists Love AMSCompass
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of artisans who've reclaimed their creative time
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, idx) => (
              <div 
                key={idx}
                className="bg-gray-50 p-8 rounded-2xl border border-gray-200 hover:border-[#1E3A5F] transition-colors"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(t.stars)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-gray-700 text-lg mb-6 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#1E3A5F] flex items-center justify-center">
                    <span className="text-white font-bold text-lg">{t.name[0]}</span>
                  </div>
                  <div>
                    <p className="font-bold text-[#1E3A5F]">{t.name}</p>
                    <p className="text-sm text-gray-500">{t.craft}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-6 bg-[#1E3A5F]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Ready to Be an Artist Again?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Stop juggling spreadsheets, calendars, and calculators. 
            Start your free 7-day trial and see what AMSCompass can do for your business.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link to={user ? '/events' : '/signup'}>
              <Button 
                className="bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold text-xl px-10 py-7 rounded-lg shadow-xl uppercase tracking-wide transition-transform hover:scale-105"
                data-testid="cta-signup"
              >
                {user ? 'Go to Dashboard' : 'Start Free Trial'}
                <ArrowRight className="w-6 h-6 ml-2" />
              </Button>
            </Link>
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-6 text-white/70 text-sm">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#22C55E]" /> 
              No credit card required
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#22C55E]" /> 
              Full access for 7 days
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#22C55E]" /> 
              Cancel anytime
            </span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <div className="flex items-center gap-3">
              <img 
                src="/ams_compass_logo.png" 
                alt="AMSCompass" 
                className="h-10 w-auto"
              />
              <div>
                <span className="text-xl font-black text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>AMS</span>
                <span className="text-xl font-black text-[#4ADE80]" style={{ fontFamily: "'Outfit', sans-serif" }}>Compass</span>
              </div>
            </div>
            <p className="text-gray-400 text-sm text-center md:text-right">
              &copy; {new Date().getFullYear()} AMSCompass by The Motley Maker LLC. 
              <br className="md:hidden" />
              <span className="md:ml-1">Empowering artisan entrepreneurs to create more and count less.</span>
            </p>
          </div>
          <div className="border-t border-gray-700 pt-6 flex flex-col md:flex-row items-center justify-center gap-6">
            <Link to="/privacy" className="text-gray-400 hover:text-white text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-gray-400 hover:text-white text-sm transition-colors">
              Terms of Service
            </Link>
            <a href="mailto:info@artisanmarketandstudio.com" className="text-gray-400 hover:text-white text-sm transition-colors">
              Contact Us
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
